import React, { useEffect, useRef, useState } from "react";
import styles from "./main.module.scss";
import { useTranslation } from "react-i18next";
import { Button, message as messageAnt, Select, SelectProps } from "antd";
import { useAdvisors } from "../../hooks/advisor.hook";
import {
  CountryExchange,
  CountryExchangeLabel,
} from "../../shared/countryExchange";
import {
  IRatingUser,
  IStrategiesInfo,
  Languages,
  StateOfTrade,
  TimeFrames,
  TradeAction,
} from "../../shared/types";
import CheckRatings from "./checkRatings/CheckRatings";
import WarningTickers from "./warningTickers/WarningTickers";
import { v4 as uuidv4 } from "uuid";
import Orders from "./orders/Orders";
import Recommendations from "./recommendations/Recommendations";
import Switches from "./switches/Switches";
import { useSettings } from "../../hooks/settings.hook";
import { useMembersRating } from "../../hooks/membersRating.hook";

const Main = () => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language as Languages;

  const {
    loading,
    getCountryWorkingAdvisors,
    getStatusAdvisors,
    setCountryWorkingAdvisorsToServer,
    setStatusAdvisorsToServer,
    error,
    clearError,
  } = useAdvisors();
  const {
    loading: loadingSettings,
    getAllStrategiesInfo,
    error: errorSettings,
    clearError: clearErrorSettings,
  } = useSettings();
  const {
    loading: loadingMembersRating,
    getAllMembersRating,
    error: errorMembersRating,
    clearError: clearErrorMembersRating,
  } = useMembersRating();

  const [strategiesInfo, setStrategiesInfo] = useState<
    IStrategiesInfo | undefined
  >();
  const [optionsSelectCountryExchange, setOptionsSelectCountryExchange] =
    useState<SelectProps["options"]>();
  const [statusAdvisors, setStatusAdvisors] = useState<boolean>();
  const [countryExchange, setCountryExchange] = useState<
    CountryExchange | undefined
  >();
  const [trackingIsEnable, setTrackingIsEnable] = useState<boolean>(true); //состояние тумблера - включено ли слежение на клиенте за рекомендациями
  const [datasIsOld, setDatasIsOld] = useState(true); //индикатор что данные по рекомендациям устарели
  const [membersRatingList, setMembersRatingList] = useState<IRatingUser[]>(); //рейтинги прогнозирующих пользователей для аналитики

  const [varForUpdate, setVarForUpdate] = useState<string>(""); //хеш переменная, чтобы обновились нужные компоненты по таймеру с перезапросом данных

  //ссылка на timeout задержки запросов по рекомендациям
  let timeoutUpdateDatas = useRef<NodeJS.Timeout>();

  //функция получение информации по существующим стратегиям
  const getStrategiesInfo = async () => {
    try {
      const data = await getAllStrategiesInfo();
      messageAnt.info(data.message);
      return data.info;
    } catch (e) {
      messageAnt.info(errorSettings);
      clearErrorSettings();
      return undefined;
    }
  };

  //получение информации по существующим стратегиям
  useEffect(() => {
    const get = async () => {
      const info = await getStrategiesInfo();
      setStrategiesInfo(info);
    };
    get();
  }, []);

  //формирование списка для селектора выбора страны фондовой биржи
  useEffect(() => {
    const optionsCountryExchange: SelectProps["options"] = [];
    Object.keys(CountryExchange).forEach((country) => {
      const value = CountryExchange[country as keyof typeof CountryExchange];
      //@ts-ignore
      const label = t(CountryExchangeLabel[country as keyof typeof CountryExchangeLabel]);
      optionsCountryExchange.push({ value: value, label: label });
    });
    setOptionsSelectCountryExchange(optionsCountryExchange);
  }, [CountryExchange, lang]);

  //выбор страны фондовой биржи
  const onChangeCountryExchange = async (value: string) => {
    setCountryExchange(value as CountryExchange);
    //запрос на сервер на смену страны советника
    const setResult = await setCountryWorkingAdvisorsToServer(
      value as CountryExchange
    );
    messageAnt.info(setResult.message);
  };

  //функция получения статуса работы советника
  const getAdvisorsIsWorking = async () => {
    try {
      const dataStatus = await getStatusAdvisors();
      messageAnt.info(dataStatus.message);
      setStatusAdvisors(dataStatus.info);
    } catch (e) {
      if (e instanceof Error) {
        messageAnt.error(e.message);
      }
      clearError();
    }
  };

  //функция получения страны рынка по которому работает советник
  const getCountryWithinWorkingAdvisors = async () => {
    try {
      const dataCountry = await getCountryWorkingAdvisors();
      messageAnt.info(dataCountry.message);
      if (dataCountry.info !== null) {
        setCountryExchange(dataCountry.info);
      } else {
        setCountryExchange(undefined);
      }
    } catch (e) {
      if (e instanceof Error) {
        messageAnt.error(e.message);
      }
      clearError();
    }
  };

  //получение с сервера статуса работы советника и страны биржи по которой он работает если работает
  useEffect(() => {
    const func = async () => {
      await getAdvisorsIsWorking();
      await getCountryWithinWorkingAdvisors();
    };
    func();
  }, []);

  //получение данных по рейтингам эффективных голосующих пользователей из БД
  const getAllMembersRatings = async () => {
    try {
      const data = await getAllMembersRating();
      messageAnt.info(data.message);
      const ratings = data.info;
      return ratings;
    } catch (e) {
      messageAnt.info(errorMembersRating);
      clearErrorMembersRating();
      return undefined;
    }
  };

  //изначальное получение данных по рейтингам эффективных голосующих пользователей из БД
  useEffect(() => {
    const get = async () => {
      const allMembersRatings = await getAllMembersRatings();
      if (allMembersRatings) {
        setMembersRatingList(allMembersRatings);
      }
    };
    get();
  }, []);

  //кнопка запуска
  const btnHandler = async () => {
    if (countryExchange === "ru") {
      messageAnt.error(
        "Работа приложения с российским рынком временно не доступна!"
      );
    } else {
      setStatusAdvisors(!statusAdvisors);
      //запрос на сервер на смену статуса работы советника
      const setResult = await setStatusAdvisorsToServer(!statusAdvisors);
      messageAnt.info(setResult.message);
    }
  };

  //периодическое обновление данных из скрипта советника на сервере
  useEffect(() => {
    if (statusAdvisors && trackingIsEnable) {
      const func = async () => {
        if (datasIsOld) {
          setDatasIsOld(false);
          if (timeoutUpdateDatas.current) {
            clearTimeout(timeoutUpdateDatas.current);
          }
          //обновляем все данные
          const newHash = uuidv4();
          setVarForUpdate(newHash);
          timeoutUpdateDatas.current = setTimeout(() => {
            setDatasIsOld(true);
          }, 60 * 1000);
        }
      };
      func();
    } else {
      setDatasIsOld(true);
      if (timeoutUpdateDatas.current) {
        clearTimeout(timeoutUpdateDatas.current);
      }
    }
  }, [statusAdvisors, trackingIsEnable, datasIsOld]);

  return (
    <div className={styles.container}>
      <div className={styles.content}>
        <div className={styles.header}>
          {/*//@ts-ignore*/}
          <p className={styles.title}>{t("MainTitle")}</p>
          <div className={styles.start}>
            <Button
              onClick={btnHandler}
              size="small"
              shape={"round"}
              disabled={!countryExchange && !statusAdvisors}
            >
              {statusAdvisors
                ? //@ts-ignore
                  t("MainTitleButtonOff")
                : //@ts-ignore
                  t("MainTitleButtonOn")}
            </Button>
          </div>
        </div>
        <div className={styles.table}>
          {/*//@ts-ignore*/}
          <h5>{t("AdvisorsTableTitle")}</h5>
          <div className={styles.blockWrap}>
            {/*//@ts-ignore*/}
            <p className={styles.title}>{t("MainChoosingCountry")}</p>
            <Select
              showSearch
              allowClear
              value={countryExchange}
              /*//@ts-ignore*/
              placeholder={t("MainChoosingCountryPlaceholder")}
              onChange={onChangeCountryExchange}
              options={optionsSelectCountryExchange}
              style={{ width: 306 }}
              size={"small"}
              disabled={statusAdvisors}
            />
          </div>
          <CheckRatings countryExchange={countryExchange} />
          <WarningTickers varForUpdate={varForUpdate} />
          <Orders
            varForUpdate={varForUpdate}
            country={countryExchange}
            strategiesInfo={strategiesInfo}
          />
          <Switches setTracking={setTrackingIsEnable} />
          <Recommendations
            varForUpdate={varForUpdate}
            strategiesInfo={strategiesInfo}
            countryExchange={countryExchange}
            membersRatingList={membersRatingList}
          />
        </div>
      </div>
    </div>
  );
};

export default Main;
